import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/tools/cable-estimator")({
  head: () => ({ meta: [{ title: "Cable Estimator" }, { name: "robots", content: "noindex" }] }),
  component: CablePage,
});

const CABLE_TYPES = [
  { name: "CAT6", cost: 0.35 },
  { name: "CAT6A", cost: 0.55 },
  { name: "RG6 Coax", cost: 0.4 },
  { name: "Speaker 16/2", cost: 0.4 },
  { name: "Speaker 16/4", cost: 0.6 },
  { name: "Speaker 14/2", cost: 0.6 },
  { name: "Speaker 14/4", cost: 0.85 },
  { name: "Fiber (OM3)", cost: 1.2 },
  { name: "HDMI 4K", cost: 2.5 },
  { name: "18/2 Control", cost: 0.35 },
];

type Run = { id: string; type: string; from: string; to: string; length: number; qty: number; cost_per_ft: number };
type Est = {
  id?: string; name: string; client_name: string | null; waste_pct: number;
  runs: Run[]; total_feet: number; total_cost: number; notes: string | null;
};

const empty = (): Est => ({ name: "", client_name: "", waste_pct: 15, runs: [], total_feet: 0, total_cost: 0, notes: "" });

function CablePage() {
  const [list, setList] = useState<Est[]>([]);
  const [current, setCurrent] = useState<Est | null>(null);

  const load = async () => {
    const { data } = await supabase.from("cable_estimates").select("*").order("created_at", { ascending: false });
    setList((data as any) || []);
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => {
    if (!current) return { feet: 0, cost: 0, byType: {} as Record<string, number> };
    const mult = 1 + (current.waste_pct || 0) / 100;
    let feet = 0, cost = 0; const byType: Record<string, number> = {};
    for (const r of current.runs) {
      const f = r.length * r.qty * mult;
      feet += f; cost += f * r.cost_per_ft;
      byType[r.type] = (byType[r.type] || 0) + f;
    }
    return { feet, cost, byType };
  }, [current]);

  const addRun = () => setCurrent(c => c && ({ ...c, runs: [...c.runs, { id: crypto.randomUUID(), type: "CAT6", from: "", to: "", length: 50, qty: 1, cost_per_ft: 0.35 }] }));
  const updateRun = (i: number, patch: Partial<Run>) => setCurrent(c => c && ({
    ...c,
    runs: c.runs.map((r, idx) => {
      if (idx !== i) return r;
      const merged = { ...r, ...patch };
      if (patch.type && patch.cost_per_ft === undefined) {
        const preset = CABLE_TYPES.find(t => t.name === patch.type);
        if (preset) merged.cost_per_ft = preset.cost;
      }
      return merged;
    }),
  }));
  const removeRun = (i: number) => setCurrent(c => c && ({ ...c, runs: c.runs.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!current || !current.name) return toast.error("Name required");
    const payload = { ...current, total_feet: totals.feet, total_cost: totals.cost };
    if (current.id) {
      const { error } = await supabase.from("cable_estimates").update(payload).eq("id", current.id);
      if (error) return toast.error(error.message);
    } else {
      const { error } = await supabase.from("cable_estimates").insert(payload as any);
      if (error) return toast.error(error.message);
    }
    toast.success("Saved"); setCurrent(null); load();
  };
  const del = async (id: string) => { if (confirm("Delete?")) { await supabase.from("cable_estimates").delete().eq("id", id); load(); } };

  if (!current) return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold">Cable Estimates</h1>
        <Button onClick={() => setCurrent(empty())}><Plus className="h-4 w-4 mr-1" />New Estimate</Button>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {list.map(e => (
          <div key={e.id} className="rounded-lg border border-border bg-card p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold">{e.name}</h3>
                <p className="text-sm text-muted-foreground">{e.client_name || "—"}</p>
                <p className="text-xs text-muted-foreground mt-1">{Math.round(e.total_feet)} ft · ${e.total_cost.toFixed(2)}</p>
              </div>
              <button onClick={() => del(e.id!)} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
            <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => setCurrent(e)}>Open</Button>
          </div>
        ))}
        {list.length === 0 && <p className="text-muted-foreground text-sm">No cable estimates yet.</p>}
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => setCurrent(null)}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
        <Button onClick={save}><Save className="h-4 w-4 mr-1" />Save</Button>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div><label className="text-xs text-muted-foreground">Name</label><Input value={current.name} onChange={e => setCurrent({ ...current, name: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Client</label><Input value={current.client_name || ""} onChange={e => setCurrent({ ...current, client_name: e.target.value })} /></div>
        <div><label className="text-xs text-muted-foreground">Waste %</label><NumberInput value={current.waste_pct} onValueChange={n => setCurrent({ ...current, waste_pct: n })} /></div>
      </div>
      <div className="mt-6 flex items-center justify-between">
        <h3 className="font-semibold">Cable Runs</h3>
        <Button size="sm" onClick={addRun}><Plus className="h-4 w-4 mr-1" />Add Run</Button>
      </div>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-muted-foreground border-b">
            <tr><th className="p-2">Type</th><th className="p-2">From</th><th className="p-2">To</th><th className="p-2 w-20">Length (ft)</th><th className="p-2 w-16">Qty</th><th className="p-2 w-24">$/ft</th><th className="p-2 w-24 text-right">Subtotal</th><th></th></tr>
          </thead>
          <tbody>
            {current.runs.map((r, i) => {
              const sub = r.length * r.qty * (1 + current.waste_pct / 100) * r.cost_per_ft;
              return (
                <tr key={r.id} className="border-b">
                  <td className="p-1">
                    <select className="w-full rounded border border-input bg-background p-1" value={r.type} onChange={e => updateRun(i, { type: e.target.value, cost_per_ft: CABLE_TYPES.find(t => t.name === e.target.value)?.cost ?? r.cost_per_ft })}>
                      {CABLE_TYPES.map(t => <option key={t.name}>{t.name}</option>)}
                    </select>
                  </td>
                  <td className="p-1"><Input value={r.from} onChange={e => updateRun(i, { from: e.target.value })} /></td>
                  <td className="p-1"><Input value={r.to} onChange={e => updateRun(i, { to: e.target.value })} /></td>
                  <td className="p-1"><NumberInput value={r.length} onValueChange={n => updateRun(i, { length: n })} /></td>
                  <td className="p-1"><NumberInput value={r.qty} onValueChange={n => updateRun(i, { qty: n })} /></td>
                  <td className="p-1"><NumberInput step="0.01" value={r.cost_per_ft} onValueChange={n => updateRun(i, { cost_per_ft: n })} /></td>
                  <td className="p-1 text-right">${sub.toFixed(2)}</td>
                  <td className="p-1"><button onClick={() => removeRun(i)} className="text-destructive"><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div className="rounded-lg border p-4 bg-card">
          <h4 className="font-semibold mb-2">By Type (with waste)</h4>
          {Object.entries(totals.byType).map(([t, f]) => (
            <div key={t} className="flex justify-between text-sm"><span>{t}</span><span>{Math.round(f)} ft</span></div>
          ))}
          {Object.keys(totals.byType).length === 0 && <p className="text-sm text-muted-foreground">Add runs to see totals</p>}
        </div>
        <div className="rounded-lg border p-4 bg-card">
          <div className="flex justify-between"><span>Total Feet</span><span className="font-semibold">{Math.round(totals.feet)} ft</span></div>
          <div className="flex justify-between mt-1"><span>Total Cost</span><span className="font-semibold text-lg">${totals.cost.toFixed(2)}</span></div>
          <p className="text-xs text-muted-foreground mt-2">Includes {current.waste_pct}% waste</p>
        </div>
      </div>
      <Textarea placeholder="Notes" className="mt-4" value={current.notes || ""} onChange={e => setCurrent({ ...current, notes: e.target.value })} />
    </div>
  );
}
